import * as Icons from "lucide-react";
import { Reveal } from "../components/motion";
import { useSite } from "../context/SiteContext";
import { Link } from "react-router-dom";

const WHY = [
  { icon: "Zap", title: "Fast", text: "Sub-second from command to sound. Dedicated music nodes in three regions keep latency invisible." },
  { icon: "ShieldCheck", title: "Reliable", text: "99.9% uptime with automatic session migration when a node sneezes. Your queue survives restarts." },
  { icon: "MousePointerClick", title: "Easy to use", text: "Buttons on every player card. Nobody needs to memorise commands to skip a track." },
  { icon: "AudioLines", title: "High-quality audio", text: "320kbps streams, volume normalisation, zero compression artefacts." },
  { icon: "BadgePercent", title: "Free", text: "Every core feature is free forever. Premium exists, but the music never stops without it." },
  { icon: "RefreshCw", title: "Constantly updated", text: "A shipped changelog every few weeks. Bugs die fast; features ship faster." },
  { icon: "Server", title: "Serious infrastructure", text: "Redundant nodes, live status page, and a team that actually watches the graphs." },
];

export default function Features() {
  const { features, site } = useSite();

  return (
    <main className="pt-16" data-testid="features-page">
      <section className="relative hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Capabilities
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="features-headline">
              FIFTEEN WEAPONS.<br /><span className="text-outline">ONE BOT.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl">
              No filler features. Every card below is something your server will use every single day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {features.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.Music;
            return (
              <Reveal key={f.id} delay={(i % 3) * 0.07} className={f.span === "wide" ? "md:col-span-8" : "md:col-span-4"}>
                <div className="group h-full card-beam rounded-xl bg-white/[0.03] border border-white/10 p-8 hover:-translate-y-1 hover:border-blurple/40 transition-[transform,border-color] duration-300" data-testid={`features-card-${i}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-lg bg-blurple/15 border border-blurple/25 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon size={22} className="text-blurple" />
                    </div>
                    <span className="font-mono text-[10px] text-white/25">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mt-6 mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#07070c]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Manifesto
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight" style={{ fontWeight: 800 }} data-testid="why-headline">
              Why GrooveMusic?
            </h2>
            <p className="text-muted-foreground mt-5 leading-relaxed max-w-md">
              Discord killed the big music bots. We built the one we'd want to use — and we only claim what we can prove. Check the status page; the numbers are public.
            </p>
            <a
              href={site?.bot_invite_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-add-button"
              className="mt-8 inline-flex items-center gap-2.5 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
            >
              <Icons.Plus size={17} strokeWidth={3} /> Add to Discord
            </a>
          </Reveal>
          <div className="space-y-1">
            {WHY.map((w, i) => {
              const Icon = Icons[w.icon];
              return (
                <Reveal key={w.title} delay={i * 0.05}>
                  <div className="flex gap-5 py-5 border-b border-white/5 group" data-testid={`why-item-${i}`}>
                    <span className="font-mono text-xs text-white/30 pt-1.5 w-6">{String(i + 1).padStart(2, "0")}</span>
                    <Icon size={18} className="text-blurple shrink-0 mt-1" />
                    <div>
                      <h3 className="font-heading font-bold text-white group-hover:text-volt transition-colors duration-200">{w.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
        <Reveal>
          <p className="text-muted-foreground mb-6">Want the full command reference instead?</p>
          <Link to="/commands" data-testid="features-to-commands" className="inline-flex items-center gap-2 border border-white/20 hover:border-volt hover:text-volt text-white font-heading font-semibold px-8 py-4 rounded-full transition-colors duration-200">
            <Icons.Terminal size={17} /> Browse commands
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
